import api from '@/lib/api';
import type { ApiResponse, PaginationMeta } from '@amuster/types';

// ── Types ───────────────────────────────────────────────────────────────────

export type ScopeType = 'campus' | 'building' | 'zone';

export interface CoverageAssignment {
  id: string;
  tenant_id: string;
  user_id: string;
  user_name: string | null;
  user_email: string | null;
  role_id: string;
  role_name: string | null;
  scope_type: ScopeType;
  scope_id: string;
  scope_name: string | null;
  valid_from: string;
  valid_until: string | null;
  is_delegation: boolean;
  created_at: string;
}

export interface DelegationRequest {
  id: string;
  tenant_id: string;
  role_id: string;
  scope_type: ScopeType;
  scope_id: string;
  from_user_id: string;
  to_user_id: string;
  start_at: string;
  end_at: string;
  status: 'requested' | 'approved' | 'rejected' | 'cancelled' | 'revoked' | 'expired';
  requested_by_user_id: string | null;
  approved_by_user_id: string | null;
  approved_at: string | null;
  reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface MyAssignmentsResponse {
  assignments: CoverageAssignment[];
  activeSessions: Array<{
    session_id: string;
    session_key: string;
    campus_id: string;
    campus_name: string;
    status: string;
    start_time: string;
  }>;
}

export interface CreateCoverageAssignmentRequest {
  user_id: string;
  role_id: string;
  scope_type: ScopeType;
  scope_id: string;
  valid_from?: string;
  valid_until?: string | null;
}

export interface CreateDelegationRequest {
  role_id: string;
  scope_type: ScopeType;
  scope_id: string;
  to_user_id: string;
  start_at: string;
  end_at: string;
  reason?: string;
}

export interface CoverageAssignmentsQuery {
  page?: number;
  limit?: number;
  scope_type?: ScopeType;
  scope_id?: string;
  role_id?: string;
  user_id?: string;
  active_only?: boolean;
}

export interface DelegationsQuery {
  page?: number;
  limit?: number;
  status?: DelegationRequest['status'];
  mine?: boolean;
}

// ── Service ─────────────────────────────────────────────────────────────────

export const ertService = {
  // ── My Assignments ────────────────────────────────────────────────────────

  getMyAssignments(activeSessionsOnly = true) {
    return api.get<ApiResponse<MyAssignmentsResponse>>('/ems/ert/my-assignments', {
      params: { active_sessions_only: activeSessionsOnly },
    });
  },

  // ── Coverage Assignments (Roster) ─────────────────────────────────────────

  listCoverageAssignments(query?: CoverageAssignmentsQuery) {
    return api.get<ApiResponse<CoverageAssignment[]> & { meta: PaginationMeta }>(
      '/ems/ert/coverage-assignments',
      { params: query }
    );
  },

  createCoverageAssignment(data: CreateCoverageAssignmentRequest) {
    return api.post<ApiResponse<CoverageAssignment>>('/ems/ert/coverage-assignments', data);
  },

  deleteCoverageAssignment(id: string) {
    return api.delete<void>(`/ems/ert/coverage-assignments/${id}`);
  },

  // ── Delegations ───────────────────────────────────────────────────────────

  listDelegations(query?: DelegationsQuery) {
    return api.get<ApiResponse<DelegationRequest[]> & { meta: PaginationMeta }>(
      '/ems/ert/delegations',
      { params: query }
    );
  },

  createDelegation(data: CreateDelegationRequest) {
    return api.post<ApiResponse<DelegationRequest>>('/ems/ert/delegations', data);
  },

  approveDelegation(id: string) {
    return api.post<ApiResponse<DelegationRequest>>(`/ems/ert/delegations/${id}/approve`);
  },

  rejectDelegation(id: string) {
    return api.post<ApiResponse<DelegationRequest>>(`/ems/ert/delegations/${id}/reject`);
  },

  cancelDelegation(id: string) {
    return api.post<ApiResponse<DelegationRequest>>(`/ems/ert/delegations/${id}/cancel`);
  },
};
